// src/component/GroupReportsAdmin2026.js
//
// 2026 グループ通報管理（事務局向け）
//   - 読み取り: groupReports2026
//   - グループ名解決: groupActivity2026Groups
//   - 通報者名解決: users
// 2025版 GroupReportsAdmin.js をベースに、対象コレクションのみ差し替えたもの。

import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  ButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  collection,
  query,
  orderBy,
  doc,
  updateDoc,
  getDoc,
  onSnapshot,
  setDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { db, auth } from "../api/firebase";
import EditIcon from "@mui/icons-material/Edit";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";

const REPORTS_COLLECTION = "groupReports2026";
const GROUPS_COLLECTION = "groupActivity2026Groups";
const CONFIG_DOC = "groupReports2026Config";

const GroupReportsAdmin2026 = () => {
  const [reports, setReports] = useState([]);
  const [groupNames, setGroupNames] = useState({});
  const [loading, setLoading] = useState(true);

  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionText, setActionText] = useState("");
  const [selectedReportId, setSelectedReportId] = useState(null);

  const [reporterNames, setReporterNames] = useState({});

  const [editTarget, setEditTarget] = useState({ reportId: null, index: null });
  const [editText, setEditText] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const [filter, setFilter] = useState("incomplete");

  const navigate = useNavigate();

  const [expandedReportId, setExpandedReportId] = useState(null);
  const [userContacts, setUserContacts] = useState({});

  const [reportEnabled, setReportEnabled] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [deletingAll, setDeletingAll] = useState(false);

  const [deleteOneTarget, setDeleteOneTarget] = useState(null); // { id, name }
  const [deletingOne, setDeletingOne] = useState(false);

  useEffect(() => {
    const ref = doc(db, "systemConfig", CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setReportEnabled(snap.exists() ? snap.data()?.reportEnabled === true : false);
      },
      (err) => {
        console.error("groupReports2026Config subscribe error:", err);
      }
    );
    return () => unsub();
  }, []);

  const handleToggleReportEnabled = async (e) => {
    const next = e.target.checked;
    setReportEnabled(next);
    setSavingConfig(true);
    try {
      await setDoc(
        doc(db, "systemConfig", CONFIG_DOC),
        { reportEnabled: next },
        { merge: true }
      );
      setSnackbar({
        open: true,
        message: next
          ? "仲間タブの三点ドット（通報ボタン）を表示にしました。"
          : "仲間タブの三点ドット（通報ボタン）を非表示にしました。",
        type: "success",
      });
    } catch (err) {
      console.error("groupReports2026Config save error:", err);
      setReportEnabled(!next);
      setSnackbar({
        open: true,
        message: "設定の保存に失敗しました。",
        type: "error",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    const checkAdmin = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/home");
        return;
      }

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const data = userDoc.data();

      if (!data?.isAdmin) {
        navigate("/home");
      }
    };

    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, REPORTS_COLLECTION), orderBy("reportedAt", "desc")),
      (snapshot) => {
        const reportList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setReports(reportList);
        setLoading(false);
      },
      (error) => {
        console.error("リアルタイム取得失敗:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchMetaInfo = async () => {
      const groupIds = [
        ...new Set(reports.map((r) => r.groupId).filter(Boolean)),
      ];
      const reporterIds = [
        ...new Set(reports.map((r) => r.reportedBy).filter(Boolean)),
      ];

      const groupMap = {};
      await Promise.all(
        groupIds.map(async (id) => {
          const docSnap = await getDoc(doc(db, GROUPS_COLLECTION, id));
          groupMap[id] = docSnap.exists()
            ? docSnap.data().name || "(無題のグループ)"
            : "不明なグループ";
        })
      );
      setGroupNames(groupMap);

      const reporterMap = {};
      await Promise.all(
        reporterIds.map(async (id) => {
          const docSnap = await getDoc(doc(db, "users", id));
          reporterMap[id] = docSnap.exists()
            ? docSnap.data().name
            : "不明なユーザー";
        })
      );
      setReporterNames(reporterMap);
    };

    if (reports.length > 0) fetchMetaInfo();
  }, [reports]);

  const handleAddHistory = async () => {
    if (!selectedReportId || !actionText.trim()) return;

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("ログインしていません");

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const authorName = userDoc.exists() ? userDoc.data().name : "不明";

      const reportRef = doc(db, REPORTS_COLLECTION, selectedReportId);
      const targetReport = reports.find((r) => r.id === selectedReportId);
      const currentHistory = targetReport?.history || [];

      await updateDoc(reportRef, {
        history: [
          ...currentHistory,
          {
            text: actionText.trim(),
            createdAt: new Date(),
            authorName,
          },
        ],
      });

      console.log("✅ 履歴追加完了");
    } catch (err) {
      console.error("❌ 履歴追加失敗:", err);
    } finally {
      setOpenActionDialog(false);
      setActionText("");
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteOneTarget?.id || deletingOne) return;
    setDeletingOne(true);
    try {
      await deleteDoc(doc(db, REPORTS_COLLECTION, deleteOneTarget.id));
      setSnackbar({
        open: true,
        message: `${deleteOneTarget.name || "対象"} さんの通報を削除しました。`,
        type: "success",
      });
    } catch (err) {
      console.error("通報削除エラー:", err);
      setSnackbar({
        open: true,
        message: "通報の削除に失敗しました。",
        type: "error",
      });
    } finally {
      setDeletingOne(false);
      setDeleteOneTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    if (bulkConfirmText.trim() !== "削除" || deletingAll) return;
    setDeletingAll(true);
    try {
      // reports は onSnapshot で購読済み。500件ずつ writeBatch で削除
      const chunks = [];
      for (let i = 0; i < reports.length; i += 500) {
        chunks.push(reports.slice(i, i + 500));
      }
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        for (const r of chunk) {
          batch.delete(doc(db, REPORTS_COLLECTION, r.id));
        }
        await batch.commit();
      }
      setSnackbar({
        open: true,
        message: `${reports.length} 件の通報を一斉削除しました。`,
        type: "success",
      });
    } catch (err) {
      console.error("一斉削除エラー:", err);
      setSnackbar({
        open: true,
        message: "一斉削除に失敗しました。",
        type: "error",
      });
    } finally {
      setDeletingAll(false);
      setBulkDeleteOpen(false);
      setBulkConfirmText("");
    }
  };

  const handleDeleteHistory = async (reportId, indexToDelete) => {
    try {
      const reportRef = doc(db, REPORTS_COLLECTION, reportId);
      const report = reports.find((r) => r.id === reportId);
      if (!report || !report.history) return;

      const updatedHistory = [...report.history];
      updatedHistory.splice(indexToDelete, 1);

      await updateDoc(reportRef, { history: updatedHistory });
      console.log("🗑️ 履歴削除完了");
    } catch (err) {
      console.error("❌ 履歴削除失敗:", err);
    }
  };

  const filteredReports = reports.filter((r) =>
    filter === "complete" ? r.finish === true : r.finish !== true
  );

  const handleAccordionChange = async (reportId, userId) => {
    setExpandedReportId((prev) => (prev === reportId ? null : reportId));

    if (!userContacts[reportId]) {
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserContacts((prev) => ({
            ...prev,
            [reportId]: {
              registrationPhone: data.registrationPhone || null,
              registrationEmail: data.registrationEmail || null,
            },
          }));
        }
      } catch (err) {
        console.error("ユーザー情報の取得エラー:", err);
      }
    }
  };

  if (loading) {
    return (
      <Box mt={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        2026 グループ通報一覧
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderColor: reportEnabled ? "#4caf50" : "rgba(0,0,0,0.12)",
          backgroundColor: reportEnabled ? "#f0fdf4" : "#fafafa",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={reportEnabled}
              onChange={handleToggleReportEnabled}
              disabled={savingConfig}
              color="success"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={700}>
                仲間タブの三点ドット（通報ボタン）を表示する
              </Typography>
              <Typography variant="caption" color="text.secondary">
                OFF にすると、ユーザーは仲間タブから新規通報できなくなります（既存の通報の閲覧・対応履歴管理はこの画面で継続可能）。
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      </Paper>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        mb={3}
      >
        <ButtonGroup variant="outlined">
          <Button
            onClick={() => setFilter("incomplete")}
            variant={filter === "incomplete" ? "contained" : "outlined"}
            color="primary"
          >
            未完
          </Button>
          <Button
            onClick={() => setFilter("complete")}
            variant={filter === "complete" ? "contained" : "outlined"}
            color="primary"
          >
            完了
          </Button>
        </ButtonGroup>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteForeverIcon />}
          disabled={reports.length === 0 || deletingAll}
          onClick={() => setBulkDeleteOpen(true)}
        >
          全通報を一斉削除（{reports.length}件）
        </Button>
      </Box>

      {filteredReports.length === 0 ? (
        <Typography color="text.secondary">通報はまだありません。</Typography>
      ) : (
        filteredReports.map((report, index) => (
          <Accordion
            key={report.id}
            expanded={expandedReportId === report.id}
            onChange={() =>
              handleAccordionChange(report.id, report.reportedUserId)
            }
            sx={{
              mb: 1,
              borderLeft:
                report.history?.length > 0
                  ? "5px solid #4caf50"
                  : "5px solid transparent",
              backgroundColor: report.history?.length > 0 ? "#f0fdf4" : "white",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">
                {index + 1}. {report.reportedUserName} ←{" "}
                {reporterNames[report.reportedBy] || "（取得中）"} /{" "}
                {groupNames[report.groupId] || "（取得中）"} /{" "}
                {report.reportReason}
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Typography variant="body2" gutterBottom>
                🕒 通報日時：{" "}
                {report.reportedAt?.toDate
                  ? report.reportedAt.toDate().toLocaleString("ja-JP")
                  : "不明"}
              </Typography>

              <Typography variant="body2">
                👤 対象者：{report.reportedUserName || "不明"}（
                {report.reportedUserId}）
              </Typography>

              {userContacts[report.id]?.registrationPhone && (
                <Typography variant="body2">
                  📞 電話番号：{userContacts[report.id].registrationPhone}
                </Typography>
              )}
              {userContacts[report.id]?.registrationEmail && (
                <Typography variant="body2">
                  ✉️ メールアドレス：{userContacts[report.id].registrationEmail}
                </Typography>
              )}

              <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedReportId(report.id);
                    setOpenActionDialog(true);
                  }}
                >
                  対応履歴を追加
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() =>
                    setDeleteOneTarget({
                      id: report.id,
                      name: report.reportedUserName,
                    })
                  }
                >
                  この通報を削除
                </Button>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!report.finish}
                      onChange={async (e) => {
                        try {
                          const reportRef = doc(
                            db,
                            REPORTS_COLLECTION,
                            report.id
                          );
                          await updateDoc(reportRef, {
                            finish: e.target.checked,
                          });
                          console.log(
                            `✅ finish を ${e.target.checked} に更新しました`
                          );
                        } catch (err) {
                          console.error("❌ finish 更新エラー:", err);
                        }
                      }}
                    />
                  }
                  label="完了"
                />
              </Box>

              {report.history && report.history.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    対応履歴
                  </Typography>
                  {report.history
                    .slice()
                    .sort(
                      (a, b) =>
                        (b.createdAt?.toDate?.()?.getTime?.() || 0) -
                        (a.createdAt?.toDate?.()?.getTime?.() || 0)
                    )
                    .map((h, i) => (
                      <Box
                        key={i}
                        sx={{
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          p: 1.5,
                          mb: 1,
                          backgroundColor: "#f9f9f9",
                          position: "relative",
                        }}
                      >
                        <Typography variant="body2" gutterBottom>
                          📝 {h.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {h.createdAt?.toDate
                            ? h.createdAt.toDate().toLocaleString("ja-JP")
                            : "日時不明"}{" "}
                          {h.authorName ? `- ${h.authorName}` : ""}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteHistory(report.id, i)}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditTarget({ reportId: report.id, index: i });
                            setEditText(h.text);
                            setOpenEditDialog(true);
                          }}
                          sx={{ position: "absolute", top: 4, right: 36 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog
        open={openActionDialog}
        onClose={() => {
          setOpenActionDialog(false);
          setActionText("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>対応履歴を追加</DialogTitle>
        <DialogContent>
          <TextField
            label="対応内容"
            multiline
            fullWidth
            minRows={3}
            value={actionText}
            onChange={(e) => setActionText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenActionDialog(false);
              setActionText("");
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleAddHistory}
            disabled={!actionText.trim()}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>対応履歴の編集</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            minRows={3}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            label="対応内容"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>キャンセル</Button>
          <Button
            onClick={async () => {
              const { reportId, index } = editTarget;
              if (reportId == null || index == null) return;

              try {
                const reportRef = doc(db, REPORTS_COLLECTION, reportId);
                const report = reports.find((r) => r.id === reportId);
                if (!report || !report.history) return;

                const updatedHistory = [...report.history];
                updatedHistory[index] = {
                  ...updatedHistory[index],
                  text: editText.trim(),
                };

                await updateDoc(reportRef, { history: updatedHistory });
                console.log("✏️ 履歴編集完了");
              } catch (err) {
                console.error("❌ 履歴編集エラー:", err);
              } finally {
                setOpenEditDialog(false);
              }
            }}
            disabled={!editText.trim()}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* この通報を削除 */}
      <Dialog
        open={!!deleteOneTarget}
        onClose={() => {
          if (deletingOne) return;
          setDeleteOneTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>この通報を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            対象：<strong>{deleteOneTarget?.name || "（対象不明）"}</strong> さんの通報
          </Typography>
          <Typography variant="body2" color="text.secondary">
            通報ドキュメントと、この通報に紐づく対応履歴も一緒に削除されます。この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOneTarget(null)}
            disabled={deletingOne}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteOne}
            color="error"
            variant="contained"
            disabled={deletingOne}
            startIcon={<DeleteOutlineIcon />}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一斉削除 */}
      <Dialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (deletingAll) return;
          setBulkDeleteOpen(false);
          setBulkConfirmText("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: "error.main" }}>
          全通報を一斉削除
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            現在の <strong>{reports.length}</strong> 件の通報をすべて削除します。対応履歴も含めて完全に消え、復元できません。
          </Alert>
          <Typography variant="body2" mb={1}>
            続行するには、下のテキストボックスに <strong>削除</strong> と入力してください。
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="削除"
            value={bulkConfirmText}
            onChange={(e) => setBulkConfirmText(e.target.value)}
            disabled={deletingAll}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkDeleteOpen(false);
              setBulkConfirmText("");
            }}
            disabled={deletingAll}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteForeverIcon />}
            disabled={bulkConfirmText.trim() !== "削除" || deletingAll}
          >
            {deletingAll ? "削除中…" : `${reports.length}件を一斉削除`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GroupReportsAdmin2026;
